import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  Input,
  NgZone,
  OnDestroy,
  signal
} from '@angular/core';
import { PipelineEventDataReportData } from '@app/pipelines/models/pipeline-event-data-report';
import { ChartID, ChartIDs, FieldViewSurveyMenuData } from '../models/field-view';
import { PipelineFieldViewDepthChartDto } from '../services/field-view-chart.service';
import { FieldViewStateService } from '../services/field-view-state.service';
import ChartUtility from '../utilities/chart.utility';

@Component({
  selector: 'app-field-view-chart-line',
  templateUrl: './field-view-chart-line.component.html',
  styleUrls: ['./field-view-chart-line.component.scss'],
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FieldViewChartLineComponent implements AfterViewInit, OnDestroy {
  private root: am5.Root;
  private chart: am5xy.XYChart;
  private legend: am5.Legend;
  private xAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;
  private yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>;

  @Input({ required: true }) chartTitle: 'Depth Profile' | 'CP Profile';
  @Input({ required: true }) chartId: ChartID;
  @Input({ required: true }) dataIsLoading: boolean;

  private readonly chartDataSignal = signal<PipelineEventDataReportData[] | PipelineFieldViewDepthChartDto>(
    []
  );

  private readonly fieldViewStateService = inject(FieldViewStateService);
  private readonly ngZone = inject(NgZone);

  private surveyMenuData = this.fieldViewStateService.surveyMenuData;

  constructor() {
    effect(() => {
      const surveyMenuData = this.surveyMenuData();
      const data = this.chartDataSignal();
      if (this.root && !this.dataIsLoading) {
        this.updateLineChartData(data, surveyMenuData);
      }
    });
  }

  @Input({ required: true })
  set chartData(value: PipelineEventDataReportData[] | PipelineFieldViewDepthChartDto) {
    this.chartDataSignal.set(value);
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.root = am5.Root.new(this.chartId);
      this.createLineChart();

      if (!this.dataIsLoading) {
        this.updateLineChartData(this.chartDataSignal(), this.surveyMenuData());
      }
    });
  }

  private createLineChart() {
    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        width: am5.p100,
        height: am5.p100,
        panY: false,
        panX: false,
        pinchZoomX: false,
        wheelY: 'none',
        paddingBottom: 25, // provide space for legend
        paddingRight: 50
      })
    );

    this.chart.set('cursor', am5xy.XYCursor.new(this.root, {}));
    ChartUtility.turnOffZoomOutButton(this.chart);

    ChartUtility.setChartTitle(this.root, this.chart, this.chartTitle);
    this.xAxis = ChartUtility.createAxis(this.root, this.chart, 'x');
    this.yAxis = ChartUtility.createAxis(this.root, this.chart, 'y');

    ChartUtility.hideAxisTooltip(this.xAxis, this.root);
    ChartUtility.hideAxisTooltip(this.yAxis, this.root);

    ChartUtility.setXAxisLabel(this.xAxis, this.root, 'KP');
    ChartUtility.setYAxisLabel(
      this.yAxis,
      this.root,
      this.chartTitle === 'CP Profile' ? 'CP (mV)' : 'Depth (m)'
    );

    this.legend = ChartUtility.createLegend(this.root, this.chart);

    this.legend.labels.template.setAll({
      fill: am5.color('#fff')
    });

    this.legend.valueLabels.template.setAll({
      fill: am5.color('#fff')
    });
  }

  private updateLineChartData(
    data: PipelineEventDataReportData[] | PipelineFieldViewDepthChartDto,
    surveyMenuData: FieldViewSurveyMenuData[]
  ) {
    if (!data) return;

    this.chart.series.clear();
    this.legend.data.clear();

    const createSeriesForData = (
      dataSeries: PipelineEventDataReportData[],
      surveyMenuData: FieldViewSurveyMenuData[],
      layerName: 'Survey' | 'Bathy'
    ) => {
      const groupedDataArray = Object.entries(
        dataSeries.reduce((acc, event) => {
          (acc[event.pipelineSurveyId] = acc[event.pipelineSurveyId] || []).push({
            pipelineSurveyId: event.pipelineSurveyId,
            kp: event.kp,
            eventNumberValue: event.eventNumberValue,
            depth: event.depth
          });
          return acc;
        }, {})
      ).map(([pipelineSurveyId, events]) => ({
        pipelineSurveyId: Number(pipelineSurveyId),
        events: events as { pipelineSurveyId: number; kp: number; eventNumberValue: number; depth: number }[]
      }));

      groupedDataArray.forEach(({ pipelineSurveyId, events }) => {
        const seriesData = events.sort((a, b) => a.kp - b.kp);
        const surveyData = surveyMenuData.find(survey => survey.pipelineSurveyId === pipelineSurveyId);

        if (surveyData) {
          const series = this.chart.series.push(
            am5xy.LineSeries.new(this.root, {
              valueYField: 'value',
              valueXField: 'kp',
              xAxis: this.xAxis,
              yAxis: this.yAxis,
              connect: false,
              tooltip: am5.Tooltip.new(this.root, {}),
              name: `${surveyData.surveyYear}/${surveyData.surveyIdent}`,
              stroke: am5.color(layerName === 'Survey' ? surveyData.eventsColor : surveyData.bathyColor)
            })
          );
          const unit = this.chartId === ChartIDs.CP_PROFILE_CHART ? 'mV' : 'm';
          series.get('tooltip').label.set('text', `[bold]{name}[/]\nKP {valueX}: {valueY}${unit}`);

          series.data.setAll(
            seriesData.map(event => ({
              kp: event.kp,
              value: this.chartId === ChartIDs.CP_PROFILE_CHART ? event.eventNumberValue : event.depth * -1
            }))
          );

          this.legend.data.push(series);
        }
      });
    };

    if (this.chartId === ChartIDs.CP_PROFILE_CHART) {
      createSeriesForData(data as PipelineEventDataReportData[], surveyMenuData, 'Survey');
    } else if (this.chartId === ChartIDs.DEPTH_PROFILE_CHART) {
      const depthData = data as PipelineFieldViewDepthChartDto;
      createSeriesForData(depthData.surveyLayerData, surveyMenuData, 'Survey');
      createSeriesForData(depthData.bathyLayerData, surveyMenuData, 'Bathy');
    }
  }

  ngOnDestroy() {
    if (this.root) {
      this.root.dispose();
    }
  }
}
